import { useEffect, useId, useState } from 'react'
import { fetchPartsPage } from '../services/inventoryApi'
import { formatCurrentStock, partTitle } from '../utils/stock'

function CompareRow({ label, source, target }) {
  return (
    <tr>
      <th>{label}</th>
      <td>{source}</td>
      <td>{target}</td>
    </tr>
  )
}

function verifyLabel(needed) {
  return needed ? '미확인' : '확인완료'
}

function partName(part) {
  return partTitle(part)
}

export default function MergePartsPanel({ source, open, onClose, onConfirm, saving = false }) {
  const titleId = useId()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [target, setTarget] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setQuery('')
    setResults([])
    setTarget(null)
    setError('')
  }, [open, source?.id])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return undefined
    const q = query.trim()
    if (!q) {
      setResults([])
      setSearching(false)
      return undefined
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      setSearching(true)
      try {
        const result = await fetchPartsPage({ q, page: 1, limit: 20 })
        if (cancelled) return
        setResults(result.items.filter((part) => part.id !== source.id && !part.mergedIntoPartId))
      } catch (err) {
        if (!cancelled) setError(err.message || '품목을 검색하지 못했습니다.')
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, query, source?.id])

  if (!open || !source) return null

  const mergedQty = target ? Number(source.quantity || 0) + Number(target.quantity || 0) : null
  const sourceName = partName(source)
  const targetName = target ? partName(target) : ''

  async function handleConfirm() {
    if (!target) {
      setError('남길 대표 품목을 선택해 주세요.')
      return
    }
    setError('')
    try {
      await onConfirm(target)
    } catch (err) {
      setError(err.message || '병합에 실패했습니다.')
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal-panel modal-panel--form merge-panel"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className="modal-title">
          중복 품목 병합
        </h2>
        <p className="muted">
          지금 보고 있는 <strong>{source.partCode}</strong>는 병합할 품목입니다. 병합 후 일반 목록에서 사라집니다.
          최종적으로 계속 사용할 대표 품목을 검색해 선택하세요.
        </p>

        <label className="field">
          <span>남길 대표 품목 검색</span>
          <input
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="관리번호, 모델, 품명, 제조사"
          />
        </label>

        {searching ? <p className="muted">검색 중...</p> : null}
        {!searching && query.trim() && results.length === 0 ? (
          <p className="empty">조건에 맞는 품목이 없습니다.</p>
        ) : null}

        {results.length > 0 ? (
          <ul className="merge-search-list">
            {results.map((part) => (
              <li key={part.id}>
                <button
                  type="button"
                  className={`merge-search-item ${target?.id === part.id ? 'is-selected' : ''}`}
                  onClick={() => setTarget(part)}
                >
                  <strong className="model-name">{partTitle(part)}</strong>
                  <span>
                    {part.partCode} · {part.manufacturer} · {formatCurrentStock(part)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {target ? (
          <>
            <div className="merge-direction" aria-live="polite">
              <div className="merge-direction__block">
                <span className="merge-direction__label">병합할 품목</span>
                <strong className="merge-direction__code">{source.partCode || '-'}</strong>
                <span className="merge-direction__model model-name">{sourceName}</span>
              </div>
              <p className="merge-direction__arrow">↓ 병합</p>
              <div className="merge-direction__block merge-direction__block--keep">
                <span className="merge-direction__label">남길 대표 품목</span>
                <strong className="merge-direction__code">{target.partCode || '-'}</strong>
                <span className="merge-direction__model model-name">{targetName}</span>
              </div>
            </div>

            <p className="merge-confirm-sentence">
              {sourceName} ({source.partCode})을
              <br />
              {targetName} ({target.partCode})에 병합합니다.
            </p>

            <table className="merge-compare">
              <thead>
                <tr>
                  <th></th>
                  <th>병합할 품목</th>
                  <th>남길 대표 품목</th>
                </tr>
              </thead>
              <tbody>
                <CompareRow label="관리번호" source={source.partCode || '-'} target={target.partCode || '-'} />
                <CompareRow label="제조사" source={source.manufacturer || '-'} target={target.manufacturer || '-'} />
                <CompareRow label="모델" source={source.model || '-'} target={target.model || '-'} />
                <CompareRow label="품명" source={source.name || '-'} target={target.name || '-'} />
                <CompareRow
                  label="현재수량"
                  source={formatCurrentStock(source)}
                  target={formatCurrentStock(target)}
                />
                <CompareRow label="단위" source={source.unit || '개'} target={target.unit || '개'} />
                <CompareRow label="위치" source={source.location || '-'} target={target.location || '-'} />
                <CompareRow
                  label="수량확인"
                  source={verifyLabel(source.quantityNeedsVerification)}
                  target={verifyLabel(target.quantityNeedsVerification)}
                />
                <CompareRow
                  label="모델확인"
                  source={verifyLabel(source.modelNeedsVerification)}
                  target={verifyLabel(target.modelNeedsVerification)}
                />
              </tbody>
            </table>
            <div className="merge-result">
              <p>
                병합 후 남는 대표 품목:
                <br />
                <strong>
                  {target.partCode} / {targetName}
                </strong>
              </p>
              <p className="merge-total">
                병합 후 대표 품목 수량: <strong>{mergedQty}{target.unit || '개'}</strong>
              </p>
            </div>
            <p className="merge-warning">
              병합할 품목은 삭제되지 않고 병합 이력으로 보존됩니다.
              <br />
              기존 입출고 이력은 변경되지 않습니다.
            </p>
          </>
        ) : null}

        {error ? <p className="form-error">{error}</p> : null}

        <div className="modal-actions">
          <button type="button" className="btn btn--ghost btn--touch" onClick={onClose} disabled={saving}>
            취소
          </button>
          <button
            type="button"
            className="btn btn--primary btn--touch"
            onClick={handleConfirm}
            disabled={saving || !target}
          >
            {saving ? '처리 중...' : '병합 실행'}
          </button>
        </div>
      </div>
    </div>
  )
}
